/**
 * Tomato Manager V2 - Firebase-based with localStorage fallback
 * This replaces the original tomatoManager.js
 */

import * as tomatoClapService from '../services/tomatoClapService';

// Cache for offline support
const countCache = {
  tomatoes: {},
  lastFetch: {}
};

// Event system for reactive updates
const dispatchCountChange = (userId, count) => {
  document.dispatchEvent(new CustomEvent('tomatoCountChanged', {
    detail: { userId, count }
  }));
};

/**
 * Get tomato count with Firebase primary, localStorage fallback
 * @param {string} userId - The user ID
 * @returns {Promise<number>} Current tomato count
 */
export const getTomatoCount = async (userId) => {
  userId = userId || 'anonymous_user';
  
  try {
    // Try Firebase first
    const counts = await tomatoClapService.getUserCounts(userId);
    
    // Cache the result
    countCache.tomatoes[userId] = counts.tomatoCount;
    countCache.lastFetch[userId] = Date.now();
    
    // Also store in localStorage for offline
    localStorage.setItem(`tomatoes_${userId}`, counts.tomatoCount.toString());
    localStorage.setItem(`lastTomatoReset_${userId}`, new Date().toISOString());
    
    return counts.tomatoCount;
  } catch (error) {
    
    // Fallback to localStorage
    const stored = localStorage.getItem(`tomatoes_${userId}`);
    if (stored !== null) {
      const count = parseInt(stored, 10);
      if (!isNaN(count) && count >= 0) {
        return count;
      }
    }
    
    // Default if nothing stored
    return 10;
  }
};

/**
 * Get tomato count synchronously from cache
 * Use this for immediate UI updates
 * @param {string} userId - The user ID
 * @returns {number} Cached tomato count
 */
export const getTomatoCountSync = (userId) => {
  userId = userId || 'anonymous_user';
  
  // Check cache first
  if (countCache.tomatoes[userId] !== undefined) {
    return countCache.tomatoes[userId];
  }
  
  // Check localStorage
  const stored = localStorage.getItem(`tomatoes_${userId}`);
  if (stored !== null) {
    const count = parseInt(stored, 10);
    if (!isNaN(count) && count >= 0) {
      countCache.tomatoes[userId] = count;
      return count;
    }
  }
  
  // Default
  return 10;
};

/**
 * Use a tomato (throw it)
 * @param {string} userId - The user ID
 * @returns {Promise<number>} New tomato count
 */
export const throwTomato = async (userId) => {
  userId = userId || 'anonymous_user';
  
  try {
    // Optimistically update cache and localStorage
    const currentCount = getTomatoCountSync(userId);
    if (currentCount <= 0) {
      throw new Error('No tomatoes available');
    }
    
    const newCount = currentCount - 1;
    countCache.tomatoes[userId] = newCount;
    localStorage.setItem(`tomatoes_${userId}`, newCount.toString());
    dispatchCountChange(userId, newCount);
    
    // Try Firebase
    const firebaseCount = await tomatoClapService.useTomato(userId);
    
    // Update with Firebase result if different
    if (firebaseCount !== newCount) {
      countCache.tomatoes[userId] = firebaseCount;
      localStorage.setItem(`tomatoes_${userId}`, firebaseCount.toString());
      dispatchCountChange(userId, firebaseCount);
    }
    
    return firebaseCount;
  } catch (error) {
    console.error('Error throwing tomato:', error);
    
    // If Firebase fails, stick with local update
    const localCount = getTomatoCountSync(userId);
    if (localCount > 0) {
      const newCount = localCount - 1;
      localStorage.setItem(`tomatoes_${userId}`, newCount.toString());
      countCache.tomatoes[userId] = newCount;
      dispatchCountChange(userId, newCount);
      return newCount;
    }
    
    throw error;
  }
};

/**
 * Refund a tomato
 * @param {string} userId - The user ID
 * @returns {Promise<number>} New tomato count
 */
export const refundTomato = async (userId) => {
  userId = userId || 'anonymous_user';
  
  try {
    // Optimistically update
    const currentCount = getTomatoCountSync(userId);
    const newCount = currentCount + 1;
    countCache.tomatoes[userId] = newCount;
    localStorage.setItem(`tomatoes_${userId}`, newCount.toString());
    dispatchCountChange(userId, newCount);
    
    // Try Firebase
    const firebaseCount = await tomatoClapService.refundTomato(userId);
    
    // Update with Firebase result
    if (firebaseCount !== newCount) {
      countCache.tomatoes[userId] = firebaseCount;
      localStorage.setItem(`tomatoes_${userId}`, firebaseCount.toString());
      dispatchCountChange(userId, firebaseCount);
    }
    
    return firebaseCount;
  } catch (error) {
    console.error('Error refunding tomato:', error);
    // Keep optimistic update
    return getTomatoCountSync(userId);
  }
};

/**
 * Add purchased tomatoes
 * @param {string} userId - The user ID
 * @param {number} amount - Number to add
 * @returns {Promise<number>} New tomato count
 */
export const addPurchasedTomatoes = async (userId, amount) => {
  userId = userId || 'anonymous_user';
  
  try {
    // Update Firebase
    const newCount = await tomatoClapService.addPurchasedTomatoes(userId, amount);
    
    // Update cache and localStorage
    countCache.tomatoes[userId] = newCount;
    localStorage.setItem(`tomatoes_${userId}`, newCount.toString());
    dispatchCountChange(userId, newCount);
    
    return newCount;
  } catch (error) {
    console.error('Error adding purchased tomatoes:', error);
    throw error;
  }
};

/**
 * Check if user has tomatoes available
 * @param {string} userId - The user ID
 * @returns {boolean} Whether user has tomatoes
 */
export const hasTomatoesAvailable = (userId) => {
  return getTomatoCountSync(userId) > 0;
};

/**
 * Initialize tomato counts on app load
 * @param {string} userId - The user ID
 */
export const initializeTomatoCounts = async (userId) => {
  if (!userId) return;
  
  try {
    // Fetch from Firebase
    await getTomatoCount(userId);
  } catch (error) {
    console.error('Error initializing tomato counts:', error);
  }
};

/**
 * Subscribe to real-time updates
 * @param {string} userId - The user ID
 * @param {function} callback - Callback when count changes
 * @returns {function} Unsubscribe function
 */
export const subscribeToTomatoCount = (userId, callback) => {
  if (!userId) return () => {};
  
  // Subscribe to Firebase changes
  const unsubscribe = tomatoClapService.subscribeToUserCounts(userId, (counts) => {
    if (counts && counts.tomatoCount !== undefined) {
      countCache.tomatoes[userId] = counts.tomatoCount;
      localStorage.setItem(`tomatoes_${userId}`, counts.tomatoCount.toString());
      dispatchCountChange(userId, counts.tomatoCount);
      callback(counts.tomatoCount);
    }
  });
  
  return unsubscribe;
};

// Re-export functions that don't need changes
export function canThrowTomato(userId, elementId) {
  userId = userId || 'anonymous_user';
  
  // Check if we've thrown a tomato at this element very recently (within 1 second)
  const throwTimeKey = `lastTomatoThrow_${elementId}_${userId}`;
  const lastThrowTime = localStorage.getItem(throwTimeKey);
  
  if (lastThrowTime) {
    const lastThrow = parseInt(lastThrowTime, 10);
    const now = Date.now();
    const elapsedMs = now - lastThrow;
    
    // If less than 1 second has passed, prevent the throw
    if (elapsedMs < 1000) {
      return false;
    }
  }
  
  // Store the current time
  localStorage.setItem(throwTimeKey, Date.now().toString());
  
  return true;
}

// For backward compatibility
export const setTomatoCount = async (userId, count) => {
  console.warn('setTomatoCount is deprecated. Counts are managed by Firebase.');
  // Store locally for offline support
  localStorage.setItem(`tomatoes_${userId}`, count.toString());
  countCache.tomatoes[userId] = count;
  dispatchCountChange(userId, count);
  return count;
};

export const resetTomatoCount = async (userId) => {
  console.warn('resetTomatoCount is deprecated. Daily resets are handled by Firebase.');
  return getTomatoCount(userId);
};